import { cn } from '@/shared/helpers/classnames'

import type { RadioProps } from './types'

/**
 * Radio component for single selection.
 *
 * Standard radio button of the Design System.
 *
 * Features:
 * - Two states: checked, unchecked
 * - Automatic styles based on state
 * - Support for all HTML input attributes
 * - Conditional classes for disabled and primary states
 *
 * @example
 * ```tsx
 * // Simple radio
 * <Radio
 *   checked={isSelected}
 *   onChange={(e) => setIsSelected(e.target.checked)}
 *   disabled={isLoading}
 * />
 * ```
 */
export const Radio = ({
  checked,
  onChange,
  disabled,
  error,
  size = 'md',
  readOnly = false,
  ...props
}: RadioProps) => {
  return (
    <input
      type="radio"
      className={cn('radio', {
        'radio-sm': size === 'sm',
        'radio-md': size === 'md',
        'radio-lg': size === 'lg',
        'radio-primary': !readOnly && checked,
        'radio-secondary': readOnly && checked,
        'radio-disabled': disabled,
        'radio-checked': checked,
        'radio-error': error,
        'radio-readonly': readOnly,
      })}
      checked={checked}
      onChange={(readOnly ? undefined : onChange) ?? (() => {})}
      disabled={disabled}
      tabIndex={readOnly ? -1 : undefined}
      aria-checked={checked}
      {...props}
    />
  )
}
